import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { TextField } from "@material-ui/core";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import SuccessToast, { useSuccessToast } from "./SuccessToast";
import { trackEvent } from "../helpers/trackEvent";
import {
  EVENT_NAME_MAX_LENGTH,
  validateEventName,
} from "../events/eventsService";
import "./ImportSpreadsheetDialog.css";
import { InitialAttendeeImportContext } from "../events/InitialAttendeeImportContext";

export const PRESET_FIELDS = {
  "First name": { type: "text" },
  "Last name": { type: "text" },
  "Full name": { type: "text" },
  "Company": { type: "text" },
  "Position": { type: "text" },
  "Email": { type: "text" },
  "Attendee type": { type: "text" },
  "QR code": { type: "qrCode" },
  "Barcode 39": { type: "barcode39" },
  "Barcode 128": { type: "barcode128" },
};

const KEEP_COLUMN_OPTION = "Keep this column";
export const REIMPORT_MODES = {
  OVERWRITE: "overwrite",
  APPEND: "append",
};

const getUniqueFieldOptions = (fieldOptions = []) =>
  Array.from(
    new Set(
      fieldOptions
        .map((field) => String(field || "").trim())
        .filter(Boolean)
    )
  );

// Check if a barcode value is compatible with Code 39
// Code 39 only supports: A-Z (uppercase), 0-9, and special chars: - . $ / + % space
const isCode39Compatible = (value) => {
  if (!value) return true; // Empty values are compatible with both
  const code39Pattern = /^[A-Z0-9\-.$\/+% ]*$/;
  return code39Pattern.test(String(value).toUpperCase()) &&
    String(value) === String(value).toUpperCase();
};

// Check if a barcode value is compatible with Code 128
// Code 128 supports ASCII characters 0-127
const isCode128Compatible = (value) => {
  if (!value) return true; // Empty values are compatible
  const str = String(value);
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i);
    if (charCode > 127) {
      return false;
    }
  }
  return true;
};

// Detect barcode type by analyzing actual values
const detectBarcodeType = (columnIndex, dataRows) => {
  if (!dataRows || dataRows.length === 0) {
    return "Barcode 128"; // Default to 128 if no data
  }

  // Sample up to first 50 rows to determine barcode type
  const samplesToCheck = Math.min(50, dataRows.length);
  let hasCode128OnlyChars = false;

  for (let i = 0; i < samplesToCheck; i++) {
    const value = dataRows[i][columnIndex];
    if (value && String(value).trim()) {
      // If any value contains characters not compatible with Code 39, it must be Code 128
      if (!isCode39Compatible(value)) {
        hasCode128OnlyChars = true;
        break;
      }
    }
  }

  return hasCode128OnlyChars ? "Barcode 128" : "Barcode 39";
};

// Smart identification of preset columns based on header names
const identifyPresetField = (
  header,
  columnIndex = null,
  dataRows = null,
  existingFieldOptions = []
) => {
  const normalizedHeader = header.toLowerCase().trim();
  if (normalizedHeader.startsWith("question: ")) return KEEP_COLUMN_OPTION;
  const exactExistingField = existingFieldOptions.find(
    (field) => field.toLowerCase().trim() === normalizedHeader
  );

  if (exactExistingField) {
    return exactExistingField;
  }

  // First name patterns
  if (normalizedHeader.includes('first') && normalizedHeader.includes('name')) {
    return "First name";
  }
  if (normalizedHeader === 'firstname' || normalizedHeader === 'first') {
    return "First name";
  }

  // Last name patterns
  if (normalizedHeader.includes('last') && normalizedHeader.includes('name')) {
    return "Last name";
  }
  if (normalizedHeader === 'lastname' || normalizedHeader === 'last' || normalizedHeader === 'surname') {
    return "Last name";
  }

  // Full name patterns
  if (normalizedHeader === 'name' || normalizedHeader === 'full name' || normalizedHeader === 'fullname') {
    return "Full name";
  }

  // Company patterns
  if (normalizedHeader === 'company' || normalizedHeader === 'organization' ||
    normalizedHeader === 'organisation' || normalizedHeader === 'employer') {
    return "Company";
  }

  // Position patterns
  if (normalizedHeader === 'position' || normalizedHeader === 'title' ||
    normalizedHeader === 'job title' || normalizedHeader === 'jobtitle' ||
    normalizedHeader === 'role') {
    return "Position";
  }

  // Email patterns
  if (normalizedHeader === 'email' || normalizedHeader === 'e-mail' ||
    normalizedHeader.includes('email') || normalizedHeader.includes('e-mail')) {
    return "Email";
  }

  // Attendee type patterns
  if (normalizedHeader === 'attendee type' || normalizedHeader === 'attendeetype' ||
    normalizedHeader === 'type' || normalizedHeader === 'participant type' ||
    normalizedHeader === 'badge type' || normalizedHeader === 'badgetype') {
    return "Attendee type";
  }

  // QR code patterns
  if (normalizedHeader === 'qr code' || normalizedHeader === 'qrcode' ||
    normalizedHeader === 'qr' || normalizedHeader.includes('qr code') ||
    normalizedHeader.includes('qrcode')) {
    return "QR code";
  }

  // Barcode 39 patterns
  if (normalizedHeader === 'barcode 39' || normalizedHeader === 'barcode39' ||
    normalizedHeader === 'code 39' || normalizedHeader === 'code39') {
    return "Barcode 39";
  }

  // Barcode 128 patterns (explicit)
  if (normalizedHeader === 'barcode 128' || normalizedHeader === 'barcode128' ||
    normalizedHeader === 'code 128' || normalizedHeader === 'code128') {
    return "Barcode 128";
  }

  // Generic barcode - analyze data to determine type
  if (normalizedHeader === 'barcode') {
    if (columnIndex !== null && dataRows) {
      return detectBarcodeType(columnIndex, dataRows);
    }
    // Default to 128 if no data available
    return "Barcode 128";
  }

  // Default to "Keep this column"
  return KEEP_COLUMN_OPTION;
};

const FieldMappingDialog = ({
  open,
  onClose,
  onSubmit,
  fileHeaders,
  dataRows,
  resetFileData,
  existingFieldOptions = [],
  useInitialOverlay = true,
  submitLabel = "Start designing your badges",
  onCancel,
}) => {
  const [mapping, setMapping] = useState({});
  const [errors, setErrors] = useState({});
  const errorMessageRef = useRef(null);
  const existingFieldOptionsKey = existingFieldOptions.join("\u0000");
  const fieldOptions = useMemo(
    () => getUniqueFieldOptions([
      ...existingFieldOptionsKey.split("\u0000"),
      ...Object.keys(PRESET_FIELDS),
    ]),
    [existingFieldOptionsKey]
  );

  useEffect(() => {
    if (open && fileHeaders) {
      // Initialize mapping with smart identification
      const initialMapping = {};
      fileHeaders.forEach((header, index) => {
        initialMapping[header] = identifyPresetField(
          header,
          index,
          dataRows,
          fieldOptions
        );
      });
      setMapping(initialMapping);
      setErrors({ duplicateHeaders: [] });
    }
  }, [open, fileHeaders, dataRows, fieldOptions]);

  // Scroll to error message when errors are set
  useEffect(() => {
    if (errors.general && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [errors]);

  const handleMappingChange = (header, value) => {
    setMapping(prev => ({
      ...prev,
      [header]: value
    }));

    // Clear errors when user makes changes
    setErrors({ duplicateHeaders: [] });
  };

  const validateMapping = () => {
    // Check that at least one name field is selected
    const selectedFields = Object.values(mapping);
    const hasFirstName = selectedFields.includes("First name");
    const hasLastName = selectedFields.includes("Last name");
    const hasFullName = selectedFields.includes("Full name");

    if (!hasFirstName && !hasLastName && !hasFullName) {
      setErrors({
        general: 'At least one name field must be selected (First name, Last name, or Full name)',
        duplicateHeaders: []
      });
      return false;
    }

    // Check for duplicate preset field mappings (excluding "Keep this column")
    const usedFields = {};
    let firstDuplicateField = null;
    let firstDuplicateHeaders = [];

    Object.entries(mapping).forEach(([header, field]) => {
      if (field !== KEEP_COLUMN_OPTION) {
        if (usedFields[field]) {
          // Only capture the first duplicate we find
          if (!firstDuplicateField) {
            firstDuplicateField = field;
            firstDuplicateHeaders = [usedFields[field], header];
          }
        } else {
          usedFields[field] = header;
        }
      }
    });

    if (firstDuplicateField) {
      setErrors({
        general: `The field "${firstDuplicateField}" is mapped to multiple columns. Each preset field can only be mapped once.`,
        duplicateHeaders: firstDuplicateHeaders
      });
      return false;
    }

    // Check that only one barcode type is selected
    const hasBarcode39 = selectedFields.includes("Barcode 39");
    const hasBarcode128 = selectedFields.includes("Barcode 128");

    if (hasBarcode39 && hasBarcode128) {
      const barcode39Header = Object.keys(mapping).find(header => mapping[header] === "Barcode 39");
      const barcode128Header = Object.keys(mapping).find(header => mapping[header] === "Barcode 128");

      setErrors({
        general: 'You can only use one barcode type. Please select either Barcode 39 or Barcode 128, not both.',
        duplicateHeaders: [barcode39Header, barcode128Header]
      });
      return false;
    }

    // Validate barcode data compatibility
    if (hasBarcode39) {
      const barcode39Header = Object.keys(mapping).find(header => mapping[header] === "Barcode 39");
      const columnIndex = fileHeaders.indexOf(barcode39Header);

      // Check all values in the column
      for (let i = 0; i < dataRows.length; i++) {
        const value = dataRows[i][columnIndex];
        if (value && String(value).trim() && !isCode39Compatible(value)) {
          setErrors({
            general: `The column "${barcode39Header}" contains values that are not compatible with Barcode 39. Code 39 only supports uppercase letters (A-Z), numbers (0-9), and special characters: - . $ / + % space. Found invalid value in row ${i + 2}: "${value}"`,
            duplicateHeaders: [barcode39Header]
          });
          return false;
        }
      }
    }

    if (hasBarcode128) {
      const barcode128Header = Object.keys(mapping).find(header => mapping[header] === "Barcode 128");
      const columnIndex = fileHeaders.indexOf(barcode128Header);

      // Check all values in the column
      for (let i = 0; i < dataRows.length; i++) {
        const value = dataRows[i][columnIndex];
        if (value && String(value).trim() && !isCode128Compatible(value)) {
          setErrors({
            general: `The column "${barcode128Header}" contains values that are not compatible with Barcode 128. Code 128 only supports ASCII characters (0-127). Found invalid value in row ${i + 2}: "${value}"`,
            duplicateHeaders: [barcode128Header]
          });
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = () => {
    if (validateMapping()) {
      onSubmit(mapping);
      trackEvent({
        action: 'attendees_spreadsheet_mapped',
        data: {
          attendeesCount: dataRows.length,
          mappedColumnsCount: Object.values(mapping).filter(field => field !== KEEP_COLUMN_OPTION).length,
        }
      })
    }
  };

  // Get available options for a header
  const getAvailableOptions = (currentHeader) => {
    const usedFields = Object.entries(mapping)
      .filter(([header, field]) => header !== currentHeader && field !== KEEP_COLUMN_OPTION)
      .map(([, field]) => field);

    return [
      KEEP_COLUMN_OPTION,
      ...fieldOptions.filter(field => !usedFields.includes(field))
    ];
  };

  const dialogClasses = {
    root: useInitialOverlay
      ? 'initial-dialog-overlay mapping-dialog-root'
      : 'mapping-dialog-root',
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      classes={dialogClasses}
    >
      <DialogContent>
        {onCancel && (
          <button
            aria-label="Close mapping dialog"
            className="button mapping-dialog-close"
            onClick={onCancel}
            type="button"
          >
            <CloseIcon className="button-icon" />
          </button>
        )}
        <h3 className="dialog-title">Map imported columns to attendee info</h3>
        <div className="print-dialog-content">
          <div className="mapping-description">
            Match each imported column to preset attendees info, or choose to keep it as a custom column.
          </div>
          {errors.general && (
            <div className="mapping-error" ref={errorMessageRef}>
              {errors.general}
            </div>
          )}

          <div className="mapping-columns-container">
            {fileHeaders.map((header, columnIndex) => {
              const currentValue = mapping[header] || KEEP_COLUMN_OPTION;
              const availableOptions = getAvailableOptions(header);

              // Include current value in available options if it exists
              const options = currentValue && !availableOptions.includes(currentValue)
                ? [currentValue, ...availableOptions]
                : availableOptions;

              // Get first 3 rows of data for this column
              const previewData = dataRows.slice(0, 3).map(row => row[columnIndex] || '');

              // Check if this header has a duplicate mapping
              const isDuplicate = errors.duplicateHeaders && errors.duplicateHeaders.includes(header);

              return (
                <div key={header} className={`mapping-column-card ${isDuplicate ? 'mapping-column-card-error' : ''}`}>
                  {/* Column preview section */}
                  <div className="mapping-column-preview">
                    {/* Header name */}
                    <div className="mapping-column-header">
                      {header}
                    </div>

                    {/* Preview data rows */}
                    {previewData.map((value, rowIndex) => (
                      <div key={rowIndex} className="mapping-data-row">
                        {value || '—'}
                      </div>
                    ))}
                  </div>

                  {/* Arrow and mapping section */}
                  <div className="mapping-arrow-section">
                    <span className="mapping-to-label">
                      to
                    </span>

                    <TextField
                      select
                      size="small"
                      value={currentValue}
                      onChange={(e) => handleMappingChange(header, e.target.value)}
                      SelectProps={{
                        native: true,
                      }}
                      variant="outlined"
                      className={`mapping-select ${currentValue !== KEEP_COLUMN_OPTION ? 'mapping-select-bold' : ''}`}
                    >
                      {options.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </TextField>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
      <DialogActions className="mapping-dialog-actions">
        <button className="button" onClick={() => {
          onClose();
          resetFileData();
        }}>
          <UploadFileIcon className="button-icon" sx={{ fontSize: '18px' }} />
          Choose another source
        </button>
        <button
          className="primary"
          onClick={handleSubmit}
        >
          {submitLabel}
        </button>
      </DialogActions>
    </Dialog>
  );
};

const ALLOWED_SPREADSHEET_EXT = ['csv', 'xlsx', 'xls'];

const ImportSpreadsheetDialog = ({
  open,
  onClose,
  onContinue,
  onEventNameChange,
  onEventNameValidate,
  variant = "initial",
  existingFieldOptions = [],
}) => {
  const isReimport = variant === "reimport";
  const initialImport = useContext(InitialAttendeeImportContext);
  const [eventbriteImport, setEventbriteImport] = useState(() => isReimport ? null : initialImport);
  const { showSuccessToast, successToast } = useSuccessToast();
  const [eventName, setEventName] = useState(() =>
    isReimport ? "" : (initialImport?.event?.name || "").slice(0, EVENT_NAME_MAX_LENGTH)
  );
  const [reimportMode, setReimportMode] = useState(REIMPORT_MODES.OVERWRITE);
  const [eventNameError, setEventNameError] = useState("");
  const [isEventNameChecking, setIsEventNameChecking] = useState(false);
  const [fileError, setFileError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileUploadMethod, setSelectedFileUploadMethod] = useState("file_picker");
  const [fileData, setFileData] = useState(null);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [uploadDragActive, setUploadDragActive] = useState(false);
  const eventNameInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadButtonRef = useRef(null);
  const uploadDragDepthRef = useRef(0);

  const handleContinue = async (event) => {
    event.preventDefault();

    const nextEventNameError = isReimport ? "" : validateEventName(eventName);
    const nextFileError = selectedFile || eventbriteImport ? "" : "Choose a spreadsheet to continue";

    setEventNameError(nextEventNameError);
    setFileError(nextFileError);

    if (nextEventNameError) {
      eventNameInputRef.current?.focus();
      return;
    }

    if (nextFileError) {
      uploadButtonRef.current?.focus();
      return;
    }

    if (!isReimport && onEventNameValidate) {
      setIsEventNameChecking(true);
      try {
        const asyncEventNameError = await onEventNameValidate(eventName.trim());
        if (asyncEventNameError) {
          setEventNameError(asyncEventNameError);
          eventNameInputRef.current?.focus();
          return;
        }
      } catch (error) {
        setEventNameError(error.message || "Could not validate event name");
        eventNameInputRef.current?.focus();
        return;
      } finally {
        setIsEventNameChecking(false);
      }
    }

    if (eventbriteImport) {
      setFileData(eventbriteImport.fileData);
      setMappingDialogOpen(true);
    } else {
      processSpreadsheetFile(selectedFile, selectedFileUploadMethod);
    }
  };

  useEffect(() => {
    if (open && !isReimport) {
      onEventNameChange?.(mappingDialogOpen ? eventName.trim() : "");
    }
  }, [eventName, isReimport, mappingDialogOpen, onEventNameChange, open]);

  const handleUploadClick = () => {
    fileInputRef.current.click();
    trackEvent({
      action: 'upload_attendees_spreadsheet_clicked',
      data: {
        uploadMethod: 'file_picker',
      },
    });
  };

  const processSpreadsheetFile = (file, uploadMethod = 'file_picker') => {
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_SPREADSHEET_EXT.includes(fileExtension)) {
      setFileError('Use an Excel (.xlsx, .xls) or CSV file');
      uploadButtonRef.current?.focus();
      return;
    }

    const isCSV = fileExtension === 'csv';

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let jsonData;

        if (isCSV) {
          // Parse CSV file
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());

          jsonData = lines.map(line => {
            // Simple CSV parsing (handles quotes)
            const cells = [];
            let cell = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              const nextChar = line[i + 1];

              if (char === '"') {
                if (inQuotes && nextChar === '"') {
                  cell += '"';
                  i++; // Skip next quote
                } else {
                  inQuotes = !inQuotes;
                }
              } else if (char === ',' && !inQuotes) {
                cells.push(cell);
                cell = '';
              } else {
                cell += char;
              }
            }
            cells.push(cell); // Add last cell

            return cells.map(c => c.trim());
          });
        } else {
          // Parse Excel file
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get the first sheet
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          // Convert to JSON
          jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        }

        if (jsonData.length === 0) {
          toast.error('The file is empty', {
            position: "bottom-center",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
          });
          return;
        }

        // First row is headers
        const headers = jsonData[0].map(h => String(h).trim()).filter(h => h);
        const dataRows = jsonData.slice(1).filter(row => row.some(cell => cell));

        if (headers.length === 0) {
          toast.error('No headers found in the file', {
            position: "bottom-center",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
          });
          return;
        }

        if (dataRows.length === 0) {
          toast.error('The file contains no data rows. Please add at least one row of data', {
            position: "bottom-center",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
          });
          return;
        }

        setFileData({
          headers,
          data: dataRows
        });
        setMappingDialogOpen(true);

        if (!isReimport) {
          showSuccessToast(
            `Imported ${dataRows.length} attendee${dataRows.length === 1 ? '' : 's'}`
          );
        }

        trackEvent({
          action: 'upload_attendees_spreadsheet',
          data: {
            attendeesCount: dataRows.length,
            columnsCount: headers.length,
            uploadMethod,
          }
        });
      } catch (error) {
        console.error('Error reading file:', error);
        toast.error('Error reading the file. Please make sure it\'s a valid Excel or CSV file', {
          position: "bottom-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
        });
      }
    };

    // Read as text for CSV, as array buffer for Excel
    if (isCSV) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setEventbriteImport(null);
    setSelectedFile(file);
    setSelectedFileUploadMethod('file_picker');
    setFileError("");
    event.target.value = '';
  };

  const handleUploadDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    uploadDragDepthRef.current += 1;
    setUploadDragActive(true);
  };

  const handleUploadDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    uploadDragDepthRef.current -= 1;
    if (uploadDragDepthRef.current <= 0) {
      uploadDragDepthRef.current = 0;
      setUploadDragActive(false);
    }
  };

  const handleUploadDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleUploadDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    uploadDragDepthRef.current = 0;
    setUploadDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    setEventbriteImport(null);
    setSelectedFile(file);
    setSelectedFileUploadMethod('drag_and_drop');
    setFileError("");
  };

  useEffect(() => {
    if (!open) {
      uploadDragDepthRef.current = 0;
      setUploadDragActive(false);
      setFileError("");
      setEventNameError("");
      setFileData(null);
      setEventbriteImport(null);
      setMappingDialogOpen(false);
      setSelectedFile(null);
      if (isReimport) {
        setReimportMode(REIMPORT_MODES.OVERWRITE);
      }
    }
  }, [isReimport, open]);

  const handleMappingSubmit = (mapping) => {
    // Replace "Keep this column" with actual header names
    const updatedMapping = {};
    Object.entries(mapping).forEach(([header, field]) => {
      if (field === KEEP_COLUMN_OPTION) {
        updatedMapping[header] = header; // Use the header name as the field name
      } else {
        updatedMapping[header] = field;
      }
    });

    // Process the data to build missing name fields
    const selectedFields = Object.values(updatedMapping);
    const hasFullName = selectedFields.includes("Full name");
    const hasFirstName = selectedFields.includes("First name");
    const hasLastName = selectedFields.includes("Last name");

    // Create a mapping from field name to column index
    const fieldToColumnIndex = {};
    Object.entries(updatedMapping).forEach(([header, field]) => {
      const columnIndex = fileData.headers.indexOf(header);
      fieldToColumnIndex[field] = columnIndex;
    });

    // Process each data row to add missing name fields
    const processedData = fileData.data.map(row => {
      const newRow = [...row];

      // Case 1: If "Full name" is not selected, build it from "First name" and "Last name"
      if (!hasFullName && hasFirstName && hasLastName) {
        const firstName = row[fieldToColumnIndex["First name"]] || '';
        const lastName = row[fieldToColumnIndex["Last name"]] || '';
        const fullName = `${firstName} ${lastName}`.trim();

        // Add full name to the row
        newRow.push(fullName);

        // Update mapping to include the new "Full name" column
        if (!fieldToColumnIndex["Full name"]) {
          fieldToColumnIndex["Full name"] = newRow.length - 1;
        }
      }

      // Case 2: If "First name" and "Last name" are not selected, build them from "Full name"
      if (hasFullName && !hasFirstName && !hasLastName) {
        const fullName = row[fieldToColumnIndex["Full name"]] || '';
        const nameParts = fullName.trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Add first name and last name to the row
        newRow.push(firstName);
        newRow.push(lastName);

        // Update mapping to include the new columns
        if (!fieldToColumnIndex["First name"]) {
          fieldToColumnIndex["First name"] = newRow.length - 2;
        }
        if (!fieldToColumnIndex["Last name"]) {
          fieldToColumnIndex["Last name"] = newRow.length - 1;
        }
      }

      return newRow;
    });

    // Update headers if new columns were added
    const updatedHeaders = [...fileData.headers];
    if (!hasFullName && hasFirstName && hasLastName) {
      updatedHeaders.push("Full name");
      updatedMapping["Full name"] = "Full name";
    }
    if (hasFullName && !hasFirstName && !hasLastName) {
      updatedHeaders.push("First name");
      updatedHeaders.push("Last name");
      updatedMapping["First name"] = "First name";
      updatedMapping["Last name"] = "Last name";
    }

    const updatedFileData = {
      headers: updatedHeaders,
      data: processedData
    };

    setMappingDialogOpen(false);
    const importResult = onContinue({
      eventName: isReimport ? undefined : eventName.trim(),
      importMode: isReimport ? reimportMode : REIMPORT_MODES.OVERWRITE,
      mapping: updatedMapping,
      fileData: updatedFileData,
    });

    if (isReimport) {
      setTimeout(() => {
        const attendeesCount =
          importResult?.fileData?.data?.length || updatedFileData.data.length;
        showSuccessToast(
          `Imported ${attendeesCount} attendee${attendeesCount === 1 ? '' : 's'}`
        );
      }, 0);
    }
  };

  const handleMappingClose = () => {
    setMappingDialogOpen(false);
  };

  const dialogClasses = {
    root: isReimport
      ? 'attendee-upload-dialog'
      : 'initial-dialog-overlay attendee-upload-dialog',
  };

  return (
    <>
      <Dialog
        open={open && !mappingDialogOpen}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        onClose={isReimport ? onClose : undefined}
        classes={dialogClasses}
      >
        <DialogContent>
          {isReimport && (
            <button
              aria-label="Close attendee upload dialog"
              className="button attendee-upload-dialog-close"
              onClick={onClose}
              type="button"
            >
              <CloseIcon className="button-icon" />
            </button>
          )}
          <h3 className="dialog-title">{eventbriteImport ? "Review your Eventbrite import" : "Import your attendee list"}</h3>
          <div className="print-dialog-content">
            <div className="import-spreadsheet-intro">
              {eventbriteImport
                ? "Confirm your event name, then map the imported attendee fields to your badges."
                : "Import a spreadsheet (Excel or CSV) with your attendee information to start designing badges"}
            </div>
            <form id="import-spreadsheet-form" className="event-name-step" noValidate onSubmit={handleContinue}>
              {isReimport ? (
                <fieldset className="reimport-mode-fieldset">
                  <legend>Import mode</legend>
                  <div className="reimport-mode-widget">
                    <label
                      className={`reimport-mode-option ${reimportMode === REIMPORT_MODES.OVERWRITE ? "is-selected" : ""}`}
                    >
                      <input
                        className="reimport-mode-input"
                        type="radio"
                        name="reimport-mode"
                        value={REIMPORT_MODES.OVERWRITE}
                        checked={reimportMode === REIMPORT_MODES.OVERWRITE}
                        onChange={() => setReimportMode(REIMPORT_MODES.OVERWRITE)}
                      />
                      <span className="reimport-mode-text">Overwrite my existing attendees</span>
                    </label>
                    <label
                      className={`reimport-mode-option ${reimportMode === REIMPORT_MODES.APPEND ? "is-selected" : ""}`}
                    >
                      <input
                        className="reimport-mode-input"
                        type="radio"
                        name="reimport-mode"
                        value={REIMPORT_MODES.APPEND}
                        checked={reimportMode === REIMPORT_MODES.APPEND}
                        onChange={() => setReimportMode(REIMPORT_MODES.APPEND)}
                      />
                      <span className="reimport-mode-text">Add to my existing attendees</span>
                    </label>
                  </div>
                </fieldset>
              ) : (
                <>
                  <label className="event-name-field">
                    <span>Event name</span>
                    <input
                      ref={eventNameInputRef}
                      type="text"
                      value={eventName}
                      onChange={(event) => {
                        setEventName(event.target.value);
                        setEventNameError("");
                      }}
                      maxLength={EVENT_NAME_MAX_LENGTH}
                      placeholder="e.g. Annual Conference 2026"
                      aria-describedby={eventNameError ? "event-name-error" : undefined}
                      aria-invalid={Boolean(eventNameError)}
                      required
                    />
                  </label>
                  {eventNameError && (
                    <div className="event-name-error" id="event-name-error" role="alert">
                      {eventNameError}
                    </div>
                  )}
                </>
              )}
              {!eventbriteImport && <button
                ref={uploadButtonRef}
                type="button"
                onClick={handleUploadClick}
                className={`button import-spreadsheet-upload${uploadDragActive ? ' import-spreadsheet-upload--drag-active' : ''}${selectedFile ? ' import-spreadsheet-upload--selected' : ''}${fileError ? ' import-spreadsheet-upload--invalid' : ''}`}
                onDragEnter={handleUploadDragEnter}
                onDragLeave={handleUploadDragLeave}
                onDragOver={handleUploadDragOver}
                onDrop={handleUploadDrop}
                aria-describedby={fileError ? "spreadsheet-file-error" : undefined}
              >
                <span className="import-spreadsheet-upload-inner">
                  <span className="import-spreadsheet-upload-icon-wrap" aria-hidden>
                    <UploadFileIcon className="import-spreadsheet-upload-icon" />
                  </span>
                  <span className="import-spreadsheet-upload-copy">
                    <span className="import-spreadsheet-upload-title">
                      {selectedFile ? "Spreadsheet selected" : "Upload a spreadsheet"}
                    </span>
                    <span className="import-spreadsheet-upload-hint">
                      {selectedFile?.name || "Click to browse, or drag and drop Excel (.xlsx, .xls) or CSV"}
                    </span>
                  </span>
                </span>
              </button>}
              {eventbriteImport && (
                <p role="status">
                  {eventbriteImport.event.name}: {eventbriteImport.fileData.data.length} attendees ready to import.
                  {isReimport && reimportMode === REIMPORT_MODES.APPEND && " Appending the same event again can create duplicate attendees. Use overwrite to refresh an existing import."}
                </p>
              )}
              {fileError && (
                <div className="event-name-error" id="spreadsheet-file-error" role="alert">
                  {fileError}
                </div>
              )}
            </form>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            className="primary"
            disabled={isEventNameChecking}
            type="submit"
            form="import-spreadsheet-form"
          >
            {isEventNameChecking ? "Checking..." : "Continue"}
          </button>
        </DialogActions>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="import-spreadsheet-file-input"
        onChange={handleFileChange}
        aria-invalid={Boolean(fileError)}
      />

      <FieldMappingDialog
        open={mappingDialogOpen}
        onClose={handleMappingClose}
        onSubmit={handleMappingSubmit}
        fileHeaders={fileData?.headers || []}
        dataRows={fileData?.data || []}
        existingFieldOptions={existingFieldOptions}
        useInitialOverlay={!isReimport}
        submitLabel={isReimport ? "Reimport attendees" : "Start designing your badges"}
        onCancel={isReimport ? onClose : undefined}
        resetFileData={() => {
          setFileData(null);
          setEventbriteImport(null);
          setSelectedFile(null);
        }}
      />
      {successToast && (
        <SuccessToast key={successToast.id} title={successToast.title} />
      )}
    </>
  );
};

export default ImportSpreadsheetDialog;

